import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router"

import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import type { Site } from "@/models/site"
import {
  type EditSiteSchema,
  editFormValuesToSiteUpdates,
  editSiteSchema,
  siteToEditFormValues,
} from "@/modules/admin/sites"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { FormSelectField } from "@/nzds/forms/form-select-field/form-select-field"
import { FormTextField } from "@/nzds/forms/form-text-field/form-text-field"
import { Form } from "@/nzds/forms/form/form"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { ResetButton } from "@/nzds/forms/reset-button/reset-button"
import { SubmitButton } from "@/nzds/forms/submit-button/submit-button"
import { useForm } from "@/nzds/forms/use-form/use-form"
import { useUpdateSite } from "@/services/site"
import { useUrl } from "@/utils/url"
import { getDisplayValue } from "@/utils/value"

import { Link as MuiLink, Stack } from "@mui/material"

interface AdminSiteEditFormProps {
  areAddressFieldsReadOnly: boolean
  buildingTypes: string[]
  buildingTypesIsPending: boolean
  site: Site
  siteId: number
  siteIsFetching: boolean
}

export const AdminSiteEditForm: React.FC<AdminSiteEditFormProps> = ({
  areAddressFieldsReadOnly,
  buildingTypes,
  buildingTypesIsPending,
  site,
  siteId,
  siteIsFetching,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { adminSitePath, adminSitesPath } = useUrl()

  const form = useForm({
    defaultValues: siteToEditFormValues(site),
    mode: "onChange",
    schema: editSiteSchema,
  })

  const navigateToShow = () => {
    void navigate(`${adminSitePath(String(siteId))}${location.search}`, {
      replace: true,
    })
  }

  const { updateSiteMutateAsync, updateSiteIsPending } = useUpdateSite({
    onSuccess: () => {
      navigateToShow()
    },
  })

  const handleSubmit: SubmitHandler<EditSiteSchema> = async (values) => {
    await updateSiteMutateAsync({
      site,
      updates: editFormValuesToSiteUpdates(values),
    })
  }

  const buildingTypeOptions = buildingTypes.map((type) => ({
    label: type,
    value: type,
  }))
  if (site.buildingType && !buildingTypes.includes(site.buildingType)) {
    buildingTypeOptions.push({
      label: site.buildingType,
      value: site.buildingType,
    })
  }

  const areFieldsDisabled =
    siteIsFetching || updateSiteIsPending || buildingTypesIsPending
  const areAddressFieldsDisabled = areFieldsDisabled || areAddressFieldsReadOnly

  return (
    <Form<EditSiteSchema>
      aria-label={t("editSite.title")}
      form={form}
      onSubmit={handleSubmit}
    >
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink
                component={Link}
                to={`${adminSitesPath}${location.search}`}
                underline="hover"
              >
                {t("admin.sites")}
              </MuiLink>
              <MuiLink
                component={Link}
                to={`${adminSitePath(String(siteId))}${location.search}`}
                underline="hover"
              >
                {t("admin.siteDetail.viewSite")}
              </MuiLink>
            </>
          }
          heading={t("editSite.title")}
          primaryActions={
            <>
              <ResetButton
                disabled={updateSiteIsPending}
                onClick={navigateToShow}
                variant="outlined"
              >
                {t("button.cancel")}
              </ResetButton>
              <SubmitButton disabled={areFieldsDisabled}>
                {t("button.save")}
              </SubmitButton>
            </>
          }
        />
        <PageCard>
          <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
            <FormTextField<EditSiteSchema>
              disabled={areFieldsDisabled}
              label={t("fields.siteName.title")}
              name="name"
              required
              variant="stacked"
            />
            <ReadonlyField
              label={t("editSite.idLabel")}
              value={getDisplayValue(site.id)}
            />
            <FormSelectField<EditSiteSchema>
              disabled={areFieldsDisabled}
              label={t("fields.buildingType.title")}
              name="buildingType"
              options={buildingTypeOptions}
              variant="stacked"
            />
            <FormTextField<EditSiteSchema>
              disabled={areFieldsDisabled}
              label={t("fields.buildingArea.title")}
              name="buildingArea"
              variant="stacked"
            />
            <ReadonlyField
              label={t("editSite.baCodeLabel")}
              value={getDisplayValue(site.baCode)}
            />
            <FormTextField<EditSiteSchema>
              disabled={areAddressFieldsDisabled}
              label={t("editSite.addressLine1Label")}
              name="addressLine1"
              variant="stacked"
            />
            <FormTextField<EditSiteSchema>
              disabled={areAddressFieldsDisabled}
              label={t("editSite.addressLine2Label")}
              name="addressLine2"
              variant="stacked"
            />
            <FormTextField<EditSiteSchema>
              disabled={areAddressFieldsDisabled}
              label={t("editSite.cityLabel")}
              name="city"
              variant="stacked"
            />
            <FormTextField<EditSiteSchema>
              disabled={areAddressFieldsDisabled}
              label={t("editSite.stateLabel")}
              name="state"
              variant="stacked"
            />
            <FormTextField<EditSiteSchema>
              disabled={areAddressFieldsDisabled}
              label={t("editSite.postalCodeLabel")}
              name="postalCode"
              variant="stacked"
            />
            <FormTextField<EditSiteSchema>
              disabled={areAddressFieldsDisabled}
              label={t("editSite.countryLabel")}
              name="country"
              variant="stacked"
            />
          </Stack>
        </PageCard>
      </Stack>
    </Form>
  )
}
